import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Checkbox } from "#components/atoms/Checkbox/Checkbox";
import { Divider } from "#components/atoms/Divider/Divider";
import { Text } from "#components/atoms/Typographies/Text";
import { Modal } from "#components/molecules/Modal/Modal";
import { OptimizationDto } from "#modules/optimizations/infra/computeOptimizations.api";
import { useAddOptimizationLines } from "#modules/optimizations/view/useAddOptimizationLines";
import { formatPartDimensions } from "#modules/parts/domain/formatPartDimensions";
import { Icon } from "#shared/icons/Icon";
import { Box } from "#shared/view/components/Box";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { ThumbnailOverlay } from "#shared/view/components/Thumbnails/ThumbnailOverlay";

const THUMBNAIL_SIZE = 48;

type CompatibleLine = OptimizationDto["quoteRequestLines"][number];

type Props = {
  lines: CompatibleLine[];
  optimizationId: string;
  quoteRequestId: string;
  onIgnore: (lineId: string) => void;
};

export const CompatibleLinesAlert = ({
  lines,
  optimizationId,
  quoteRequestId,
  onIgnore,
}: Props) => {
  const { t } = useTranslation();
  const { addOptimizationLines } = useAddOptimizationLines({ quoteRequestId });
  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [checkedLineIds, setCheckedLineIds] = useState<ReadonlySet<string>>(() => new Set());
  const isSingleLine = lines.length === 1;
  const singleLine = lines[0];

  if (!singleLine) {
    return null;
  }

  const onOpenModal = () => {
    setCheckedLineIds(new Set());
    openModal();
  };

  const onCloseModal = () => {
    closeModal();
    setCheckedLineIds(new Set());
  };

  const toggleLine = (lineId: string) => {
    setCheckedLineIds((current) => {
      const next = new Set(current);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  };

  const submitAdd = (lineIds: string[]) => {
    if (lineIds.length === 0) {
      return;
    }

    addOptimizationLines({ optimizationId, lineIds });
    onCloseModal();
  };

  return (
    <>
      <Flex align="center" gap={spacing[2]} px={spacing[6]} py={spacing[3]}>
        {isSingleLine ? (
          <ThumbnailOverlay
            part={singleLine.part}
            size={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
          />
        ) : (
          <Box
            aria-hidden
            bg={colors.bg.elevation.quaternary}
            w={THUMBNAIL_SIZE}
            h={THUMBNAIL_SIZE}
            style={{
              borderRadius: radius.xs,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon name="plus" size={16} color={colors.icon.primaryDefault} />
          </Box>
        )}
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text size="small" type="medium">
            {isSingleLine
              ? t("optimizations.compatible.singleTitle", { name: singleLine.part.name })
              : t("optimizations.compatible.severalTitle", { count: lines.length })}
          </Text>
          <Text size="tiny" color={colors.text.secondaryDefault}>
            {isSingleLine
              ? t("optimizations.compatible.singleDescription")
              : t("optimizations.compatible.severalDescription")}
          </Text>
        </Stack>
        <Flex align="center" gap={spacing[4]} style={{ flexShrink: 0 }}>
          {isSingleLine ? (
            <Button variant="link" size="md" onClick={() => onIgnore(singleLine.id)}>
              {t("optimizations.compatible.ignore")}
            </Button>
          ) : null}
          <Button
            variant="tertiary"
            size="md"
            onClick={isSingleLine ? () => submitAdd([singleLine.id]) : onOpenModal}
          >
            {isSingleLine
              ? t("optimizations.compatible.add")
              : t("optimizations.compatible.choose")}
          </Button>
        </Flex>
      </Flex>
      {isSingleLine ? null : (
        <Modal
          opened={isModalOpened}
          title={t("optimizations.compatible.severalTitle", { count: lines.length })}
          subtitle={t("optimizations.compatible.modalSubtitle")}
          primaryText={t("optimizations.compatible.add")}
          closeText={t("common.cancel")}
          onClose={onCloseModal}
          onPrimary={() => submitAdd(Array.from(checkedLineIds))}
          primaryDisabled={checkedLineIds.size === 0}
        >
          <Stack gap={spacing[3]}>
            {lines.map((line, index) => {
              const typeLabel = line.part.type ? t(`part.type.${line.part.type}`) : "";
              const dimensions = formatPartDimensions(line.part);

              return (
                <Stack key={line.id} gap={spacing[3]}>
                  {index > 0 ? <Divider type="secondary" /> : null}
                  <Flex align="center" gap={spacing[2]}>
                    <ThumbnailOverlay
                      part={line.part}
                      size={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
                    />
                    <Stack gap={spacing[1]} style={{ flex: 1, minWidth: 0 }}>
                      <Text size="small" type="medium" truncate>
                        {line.part.name}
                      </Text>
                      {typeLabel || dimensions ? (
                        <Flex align="center" gap={spacing[1.5]} style={{ minWidth: 0 }}>
                          {typeLabel ? (
                            <Text size="tiny" color={colors.text.secondaryDefault}>
                              {typeLabel}
                            </Text>
                          ) : null}
                          {typeLabel && dimensions ? (
                            <Divider
                              orientation="vertical"
                              style={{ height: spacing[4], alignSelf: "center" }}
                            />
                          ) : null}
                          {dimensions ? (
                            <Text size="tiny" color={colors.text.secondaryDefault} truncate>
                              {dimensions}
                            </Text>
                          ) : null}
                        </Flex>
                      ) : null}
                    </Stack>
                    <Checkbox
                      checked={checkedLineIds.has(line.id)}
                      onChange={() => toggleLine(line.id)}
                      aria-label={line.part.name}
                    />
                  </Flex>
                </Stack>
              );
            })}
          </Stack>
        </Modal>
      )}
    </>
  );
};
